import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Cookie,
  FileText,
  Globe,
  Lock,
  Mail,
  Scale,
  ShieldCheck,
} from "lucide-react";
import Seo from "../components/Seo";
import { useLang } from "../i18n/useLang";
import "../Styles/PrivacyPolicy.css";
import { localizedPath } from "../i18n/routes";

const ICONS = {
  usage: ArrowUpRight,
  cookie: Cookie,
  document: FileText,
  globe: Globe,
  lock: Lock,
  mail: Mail,
  rights: Scale,
  shield: ShieldCheck,
};

function PrivacyPolicy() {
  const { t, dir, lang } = useLang();
  const content = t.privacy;

  return (
    <>
      <Seo
        title={content.seo.title}
        description={content.seo.description}
      />

      <section className="section privacyPage">
        <div className="container privacyPage__shell" dir={dir}>
          <header className="privacyHero card">
            <div className="privacyHero__copy">
              <span className="privacyHero__eyebrow">{content.hero.eyebrow}</span>
              <h1 className="privacyHero__title">{content.hero.title}</h1>
              <p className="privacyHero__intro">{content.hero.intro}</p>

              <div className="privacyHero__meta">
                <div>
                  <span>{content.hero.effectiveDateLabel}</span>
                  <strong>{content.hero.effectiveDate}</strong>
                </div>
                <div>
                  <span>{content.hero.updatedLabel}</span>
                  <strong>{content.hero.updatedDate}</strong>
                </div>
                <div>
                  <span>{content.hero.websiteLabel}</span>
                  <strong>{content.hero.websiteValue}</strong>
                </div>
                <div>
                  <span>{content.hero.contactLabel}</span>
                  <strong>{content.hero.contactValue}</strong>
                </div>
              </div>
            </div>

              <div className="privacyHero__facts">
              {content.quickFacts.map((fact) => {
                const Icon = ICONS[fact.icon] || ShieldCheck;

                return (
                  <article key={fact.title} className="privacyFact">
                    <span className="privacyFact__icon">
                      <Icon size={18} />
                    </span>
                    <div>
                      <h2>{fact.title}</h2>
                      <p>{fact.text}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </header>

          <div className="privacyLayout">
            <aside className="privacyToc card" aria-label={content.jumpLabel}>
              <p className="privacyToc__label">{content.jumpLabel}</p>
              <nav className="privacyToc__nav">
                {content.sections.map((section) => (
                  <a key={section.id} href={`#${section.id}`} className="privacyToc__link">
                    <span>{section.number}</span>
                    <span>{section.title}</span>
                  </a>
                ))}
              </nav>
              <Link to={localizedPath("/", lang)} className="btn privacyToc__home">
                {content.backHome}
              </Link>
            </aside>

            <div className="privacySections">
              {content.sections.map((section) => {
                const Icon = ICONS[section.icon] || FileText;

                return (
                  <article key={section.id} id={section.id} className="privacySection card">
                    <div className="privacySection__head">
                      <span className="privacySection__number">{section.number}</span>
                      <div>
                        <span className="privacySection__icon">
                          <Icon size={18} />
                        </span>
                        <h2>{section.title}</h2>
                      </div>
                    </div>

                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph} className="privacySection__text">
                        {paragraph}
                      </p>
                    ))}

                    {section.subsections?.map((subsection) => (
                      <section key={subsection.title} className="privacySubsection">
                        <h3>{subsection.title}</h3>
                        <ul className="privacyList">
                          {subsection.items.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                        {subsection.note && (
                          <p className="privacySection__note">{subsection.note}</p>
                        )}
                      </section>
                    ))}

                    {section.preface && <p className="privacySection__text">{section.preface}</p>}

                    {section.items && (
                      <ul className="privacyList">
                        {section.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )}

                    {section.note && <p className="privacySection__note">{section.note}</p>}

                    {section.contactCard && (
                      <div className="privacyContact">
                        <h3>{section.contactCard.company}</h3>
                        {section.contactCard.addressLines.map((line) => (
                          <p key={line}>{line}</p>
                        ))}
                        <a href={`mailto:${section.contactCard.email}`}>{section.contactCard.email}</a>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default PrivacyPolicy;
